
import React, {
    useEffect,
    useState
} from "react";

import Swal from "sweetalert2";

import {
    FaEdit,
    FaTrash,
    FaPlus
} from "react-icons/fa";

import {
    useNavigate
} from "react-router-dom";

import API_BASE_URL from "../config/api";


const Blogs = () => {

    /* =========================================================
       NAVIGATION
    ========================================================= */

    const navigate = useNavigate();


    /* =========================================================
       STATES
    ========================================================= */

    const [blogs, setBlogs] = useState([]);

    const [loading, setLoading] = useState(false);


    /* =========================================================
       GET ALL BLOGS
    ========================================================= */

    const fetchBlogs = async () => {

        try {

            setLoading(true);


            const response = await fetch(
                `${API_BASE_URL}/admin/blogs`,
                {
                    method: "GET",

                    headers: {
                        Accept: "application/json"
                    },

                    credentials: "include"
                }
            );


            const data = await response.json();


            console.log(
                "BLOG API RESPONSE:",
                data
            );


            if (
                response.ok &&
                data.success
            ) {

                /*
                 * Laravel Controller:
                 *
                 * 'blogs' => $blogs
                 *
                 * Therefore:
                 * data.blogs
                 */

                setBlogs(
                    Array.isArray(data.blogs)
                        ? data.blogs
                        : []
                );


            } else {

                setBlogs([]);

                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text:
                        data.message ||
                        "Unable to fetch blogs"
                });

            }


        } catch (error) {

            console.error(
                "Fetch blogs error:",
                error
            );


            setBlogs([]);


            Swal.fire({
                icon: "error",
                title: "Server Error",
                text:
                    "Unable to connect with server"
            });


        } finally {

            setLoading(false);

        }

    };


    /* =========================================================
       INITIAL LOAD
    ========================================================= */

    useEffect(() => {

        fetchBlogs();

    }, []);


    /* =========================================================
       ADD BLOG
    ========================================================= */

    const handleAdd = () => {

        navigate("/blogs/add");

    };


    /* =========================================================
       EDIT BLOG
    ========================================================= */

    const handleEdit = (id) => {

        navigate(
            `/blogs/edit/${id}`
        );

    };


    /* =========================================================
       DELETE BLOG
    ========================================================= */

    const handleDelete = async (id) => {

        const result =
            await Swal.fire({

                icon: "warning",

                title: "Delete Blog?",

                text:
                    "This blog will be permanently deleted.",

                showCancelButton: true,

                confirmButtonText:
                    "Yes, Delete",

                cancelButtonText:
                    "Cancel",

                reverseButtons: true

            });


        if (
            !result.isConfirmed
        ) {

            return;

        }


        try {

            setLoading(true);


            const response =
                await fetch(
                    `${API_BASE_URL}/admin/blogs/${id}`,
                    {
                        method: "DELETE",

                        headers: {
                            Accept:
                                "application/json"
                        },

                        credentials:
                            "include"
                    }
                );


            const data =
                await response.json();


            console.log(
                "DELETE BLOG RESPONSE:",
                data
            );


            if (
                response.ok &&
                data.success
            ) {

                await Swal.fire({

                    icon: "success",

                    title: "Deleted",

                    text:
                        data.message ||
                        "Blog deleted successfully",

                    timer: 1500,

                    showConfirmButton:
                        false

                });


                /*
                 * Refresh blog list
                 */

                await fetchBlogs();


            } else {

                Swal.fire({

                    icon: "error",

                    title: "Error",

                    text:
                        data.message ||
                        "Unable to delete blog"

                });

            }


        } catch (error) {

            console.error(
                "Delete blog error:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Server Error",

                text:
                    "Something went wrong while deleting blog"

            });


        } finally {

            setLoading(false);

        }

    };


    /* =========================================================
       DESCRIPTION PREVIEW
    ========================================================= */

    const getDescriptionPreview = (
        description
    ) => {

        if (!description) {

            return "";

        }


        const tempDiv =
            document.createElement(
                "div"
            );


        tempDiv.innerHTML =
            description;


        const text =
            tempDiv.textContent ||
            tempDiv.innerText ||
            "";


        if (
            text.length > 100
        ) {

            return (
                text.substring(
                    0,
                    100
                ) + "..."
            );

        }


        return text;

    };


    /* =========================================================
       RENDER
    ========================================================= */

    return (

        <div
            className="container-fluid py-4"
            style={{
                maxHeight: "500px",
                overflowX: "auto",
                overflowY: "auto"
            }}
        >


            {/* =================================================
                HEADER
            ================================================= */}

            <div
                className="
                    d-flex
                    justify-content-between
                    align-items-center
                    mb-4
                "
            >

                <div>

                    <h3 className="mb-1">
                        Blogs
                    </h3>

                    <p className="text-muted mb-0">
                        Manage website blogs
                    </p>

                </div>


                {/* ADD BLOG */}

                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAdd}
                >

                    <FaPlus
                        className="me-2"
                    />

                    Add Blog

                </button>

            </div>


            {/* =================================================
                BLOG TABLE
            ================================================= */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                "
            >

                <div className="card-body">


                    {/* =================================================
                        LOADING
                    ================================================= */}

                    {loading ? (

                        <div
                            className="
                                text-center
                                py-5
                            "
                        >

                            <div
                                className="
                                    spinner-border
                                    text-primary
                                "
                                role="status"
                            />

                            <div className="mt-2">
                                Loading blogs...
                            </div>

                        </div>


                    ) : blogs.length === 0 ? (

                        /* =================================================
                           NO BLOGS
                        ================================================= */

                        <div
                            className="
                                text-center
                                py-5
                                text-muted
                            "
                        >

                            No blogs found.

                        </div>


                    ) : (

                        /* =================================================
                           BLOG TABLE
                        ================================================= */

                        <div
                            className="
                                table-responsive
                            "
                        >

                            <table
                                className="
                                    table
                                    table-bordered
                                    table-hover
                                    align-middle
                                "
                            >

                                <thead
                                    className="
                                        table-light
                                    "
                                >

                                    <tr>

                                        <th width="70">
                                            #
                                        </th>

                                        <th width="100">
                                            Image
                                        </th>

                                        <th>
                                            Title
                                        </th>

                                        <th>
                                            Author
                                        </th>

                                        <th width="100">
                                            Order
                                        </th>

                                        <th width="130">
                                            Date
                                        </th>

                                        <th width="130">
                                            Action
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {blogs.map(
                                        (
                                            blog,
                                            index
                                        ) => (

                                            <tr
                                                key={
                                                    blog.blog_id
                                                }
                                            >

                                                {/* NUMBER */}

                                                <td>
                                                    {
                                                        index + 1
                                                    }
                                                </td>


                                                {/* IMAGE */}

                                                <td>

                                                    {
                                                        blog.image_url
                                                            ? (

                                                                <img
                                                                    src={
                                                                        blog.image_url
                                                                    }
                                                                    alt={
                                                                        blog.title ||
                                                                        "Blog"
                                                                    }
                                                                    style={{
                                                                        width:
                                                                            "70px",

                                                                        height:
                                                                            "50px",

                                                                        objectFit:
                                                                            "cover",

                                                                        borderRadius:
                                                                            "5px",

                                                                        border:
                                                                            "1px solid #ddd"
                                                                    }}
                                                                />

                                                            )
                                                            : (

                                                                <span
                                                                    className="
                                                                        text-muted
                                                                    "
                                                                >
                                                                    No Image
                                                                </span>

                                                            )
                                                    }

                                                </td>


                                                {/* TITLE + DESCRIPTION */}

                                                <td>

                                                    <strong>
                                                        {
                                                            blog.title
                                                        }
                                                    </strong>


                                                    <div
                                                        className="
                                                            text-muted
                                                            small
                                                            mt-1
                                                        "
                                                    >

                                                        {
                                                            getDescriptionPreview(
                                                                blog.description
                                                            )
                                                        }

                                                    </div>

                                                </td>


                                                {/* AUTHOR */}

                                                <td>
                                                    {
                                                        blog.author
                                                    }
                                                </td>


                                                {/* SORT ORDER */}

                                                <td>
                                                    {
                                                        blog.sort_order
                                                    }
                                                </td>


                                                {/* DATE */}

                                                <td>
                                                    {
                                                        blog.date_added
                                                    }
                                                </td>


                                                {/* ACTION */}

                                                <td>

                                                    <div
                                                        className="
                                                            d-flex
                                                            gap-2
                                                        "
                                                    >

                                                        {/* EDIT */}

                                                        <button
                                                            type="button"
                                                            className="
                                                                btn
                                                                btn-sm
                                                                btn-outline-primary
                                                            "
                                                            onClick={() =>
                                                                handleEdit(
                                                                    blog.blog_id
                                                                )
                                                            }
                                                            title="Edit"
                                                            disabled={
                                                                loading
                                                            }
                                                        >

                                                            <FaEdit />

                                                        </button>


                                                        {/* DELETE */}

                                                        <button
                                                            type="button"
                                                            className="
                                                                btn
                                                                btn-sm
                                                                btn-outline-danger
                                                            "
                                                            onClick={() =>
                                                                handleDelete(
                                                                    blog.blog_id
                                                                )
                                                            }
                                                            title="Delete"
                                                            disabled={
                                                                loading
                                                            }
                                                        >

                                                            <FaTrash />

                                                        </button>

                                                    </div>

                                                </td>

                                            </tr>

                                        )
                                    )}

                                </tbody>

                            </table>

                        </div>

                    )}

                </div>

            </div>

        </div>

    );

};


export default Blogs;
